import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAuth } from "@/contexts/AuthContext";
import {
  Calendar,
  Heart,
  Keyboard,
  Moon,
  Shield,
  Users,
  Waves,
} from "lucide-react";
import { useEffect, useState } from "react";

const STORAGE_KEY = "mare_onboarding_seen";

interface TourStep {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
}

const stepsForRole: Record<string, TourStep[]> = {
  default: [
    {
      icon: Waves,
      title: "Bem-vindo ao Instituto Maré!",
      description:
        "Este é o seu painel de gestão clínica. Vamos fazer um tour rápido pelas funcionalidades principais.",
    },
    {
      icon: Keyboard,
      title: "Navegação rápida (⌘K)",
      description:
        "Use Cmd+K (ou Ctrl+K) para abrir a paleta de comandos e navegar rapidamente entre as páginas.",
    },
    {
      icon: Moon,
      title: "Modo escuro",
      description:
        "Alterne entre modo claro e escuro no menu lateral para sua preferência visual.",
    },
    {
      icon: Shield,
      title: "Segurança",
      description:
        "Recomendamos ativar a autenticação em dois fatores (2FA) em Configurações > Segurança.",
    },
  ],
  ADMIN: [
    {
      icon: Waves,
      title: "Bem-vindo, Administrador!",
      description:
        "Você tem acesso completo ao sistema. Vamos conhecer as funcionalidades principais.",
    },
    {
      icon: Users,
      title: "Gestão de usuários",
      description:
        "Gerencie psicólogos, recepcionistas e pacientes em Administração > Usuários.",
    },
    {
      icon: Calendar,
      title: "Agenda e pacientes",
      description:
        "Acompanhe a agenda de consultas e os prontuários de todos os pacientes.",
    },
    {
      icon: Keyboard,
      title: "Atalho rápido (⌘K)",
      description:
        "Use Cmd+K para abrir a busca rápida e navegar entre páginas instantaneamente.",
    },
  ],
  PACIENTE: [
    {
      icon: Heart,
      title: "Bem-vindo ao seu espaço!",
      description:
        "Aqui você acompanha suas sessões, registra emoções e acessa seus documentos.",
    },
    {
      icon: Waves,
      title: "Diário emocional",
      description:
        "Registre como está se sentindo no calendário de humor — ajuda seu acompanhamento terapêutico.",
    },
    {
      icon: Shield,
      title: "Sua privacidade",
      description:
        "Seus dados são protegidos por LGPD. Ative 2FA para mais segurança.",
    },
  ],
};

export function OnboardingTour() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!user) return;
    const key = `${STORAGE_KEY}_${user.id}`;
    const seen = localStorage.getItem(key);
    if (!seen) {
      // Small delay for layout to render
      const timer = setTimeout(() => setOpen(true), 800);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const dismiss = () => {
    if (user) {
      localStorage.setItem(`${STORAGE_KEY}_${user.id}`, "true");
    }
    setOpen(false);
    setStep(0);
  };

  const steps = stepsForRole[user?.role || ""] || stepsForRole.default;

  const currentStep = steps[step];
  const isLast = step === steps.length - 1;
  const Icon = currentStep?.icon;

  if (!currentStep) return null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) dismiss();
      }}
    >
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader className="text-center">
          <div className="mx-auto mb-4 h-16 w-16 rounded-full bg-brand-azul-profundo/10 flex items-center justify-center">
            <Icon className="h-8 w-8 text-brand-azul-profundo" />
          </div>
          <DialogTitle className="text-lg">{currentStep.title}</DialogTitle>
          <DialogDescription className="text-sm pt-2">
            {currentStep.description}
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex justify-center gap-1.5 py-2">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === step ? "w-6 bg-brand-azul-profundo" : "w-1.5 bg-slate-200"
              }`}
            />
          ))}
        </div>

        <DialogFooter className="flex-row gap-2 sm:justify-between">
          <Button variant="ghost" size="sm" onClick={dismiss}>
            Pular tour
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setStep((s) => s - 1)}
              >
                Anterior
              </Button>
            )}
            {isLast ? (
              <Button size="sm" onClick={dismiss}>
                Começar!
              </Button>
            ) : (
              <Button size="sm" onClick={() => setStep((s) => s + 1)}>
                Próximo
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
